import React, { useEffect, useState } from 'react';
import { Btn, Loading, Msg, Section, panelPost, useAdminGet } from '../../kit';
import { inputCls } from '../emailKit';
import { Sender } from './shared';

/**
 * The From addresses a campaign may use (GET email/senders, POST
 * email/senders_save). A short approved list rather than a free field: every
 * address must be one SendGrid will send from (our authenticated domain), and
 * a From outside it reads as spoofing.
 */
export const SendersEditor: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const q = useAdminGet('email', 'senders');
  const d: any = q.data || {};
  const [rows, setRows] = useState<Sender[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { if (d.senders) setRows(d.senders.map((s: Sender) => ({ ...s }))); }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const domain = String(d.domain || '@universitytech.io');
  const max = Number(d.max || 10);
  const set = (i: number, patch: Partial<Sender>) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true); setMsg(null);
    const { error } = await panelPost('email', 'senders_save', {
      senders: rows.map(r => ({ email: r.email.trim().toLowerCase(), name: r.name.trim() })),
    });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    q.reload();   // the saved (normalised) list comes back and resets the rows
    setMsg({ kind: 'ok', text: 'Saved. New campaigns pick From from this list.' });
    onSaved();
  };
  const dirty = JSON.stringify(rows) !== JSON.stringify(d.senders || []);
  return (
    <Section title="Approved senders"
      right={<Btn small onClick={() => d.defaults && setRows(d.defaults.map((s: Sender) => ({ ...s })))}
        disabled={!d.defaults}>Back to the defaults</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        The addresses a campaign can be sent From — each must end in {domain} (the domain SendGrid is set up to send
        for). The name is filled in as the From name when the address is picked; it can still be changed per send.
      </p>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading && !q.data ? <Loading /> : (
        <div className="flex flex-col gap-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap">
              <input value={r.email} onChange={e => set(i, { email: e.target.value })}
                placeholder={`name${domain}`} className={`${inputCls} !w-72`} aria-label="Sender address" />
              <input value={r.name} onChange={e => set(i, { name: e.target.value })} maxLength={100}
                placeholder="From name" className={`${inputCls} !w-72`} aria-label="Sender name" />
              <Btn small onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} disabled={rows.length <= 1}>Remove</Btn>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Btn small onClick={() => setRows(rs => [...rs, { email: '', name: '' }])} disabled={rows.length >= max}>
              + Add a sender
            </Btn>
            <Btn small kind="primary" onClick={save} disabled={busy || !dirty || !rows.length}>
              {busy ? 'Saving…' : 'Save'}
            </Btn>
          </div>
        </div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
    </Section>
  );
};
